import React from 'react'
import { findDOMNode } from "react-dom"
import { genKey } from 'draft-js'
import addMention from '../modifiers/addMention'
import BaseComponent from "../../../core/gd-components/base-component/base-component"

import {MentionSuggestionsProps} from './@types'
import {getActiveOffsetKey, getMentionOptionId, setAriaClosed, setAriaOpened} from './helpers'

// Dropdown open/close, keyboard navigation and mention selection - MentionSuggestions extends it
export default class MentionSuggestionsDropdown extends BaseComponent {

    declare props : Readonly<MentionSuggestionsProps>

    uniqKey                     : string
    scrollRef                   = React.createRef<any>()
    lastSearchValue             : string
    lastSelectionIsInsideWord   : any

    constructor(props:MentionSuggestionsProps) {
        super(props)
        this.uniqKey = genKey()

        this.state = {
            focusedOptionIndex: 0,
            isActive: false
        }

        this._bind('onKeyDown', 'onDownArrow', 'onTab', 'onUpArrow', 'onEscape',
            'onMentionSelect', 'onMentionFocus', 'commitSelection', 'openDropdown', 'closeDropdown')
    }

    onDownArrow(keyboardEvent) {
        keyboardEvent.preventDefault()
        keyboardEvent.stopPropagation()
        const newIndex = this.state.focusedOptionIndex + 1
        this.onMentionFocus(
            newIndex >= this.props.suggestions.length ? 0 : newIndex,
            ()=>{this.updatePopover()})
    }

    onTab(keyboardEvent) {
        keyboardEvent.preventDefault()
        keyboardEvent.stopPropagation()
        this.commitSelection()
    }

    onUpArrow(keyboardEvent) {
        keyboardEvent.preventDefault()
        keyboardEvent.stopPropagation()

        if (this.props.suggestions.length > 0) {
            const newIndex = this.state.focusedOptionIndex - 1
            this.onMentionFocus(
                newIndex < 0 ? this.props.suggestions.length - 1 : newIndex,
                ()=>{this.updatePopover()})
        }
    }

    onEscape(keyboardEvent) {
        keyboardEvent.preventDefault()

        this.props.store.escapeSearch(getActiveOffsetKey(this.lastSelectionIsInsideWord))
        this.closeDropdown()

        // to force a re-render of the outer component to change the aria props
        this.props.store.setEditorState(this.props.store.getEditorState())
    }

    onMentionSelect(mention) {
        // Note: This can happen in case a user typed @xxx (invalid mention) and
        // then hit Enter. Then the mention will be undefined.
        if (!mention) {
            return
        }

        this.closeDropdown()
        const processAddMention = ()=>{
            if (this.props.onAddMention) {
                this.props.onAddMention(mention)
            }

            const newEditorState = addMention(
                this.props.store.getEditorState(),
                mention,
                this.props.mentionPrefix,
                this.props.mentionTrigger,
                this.props.entityMutability
            )
            this.props.store.setEditorState(newEditorState)
        }

        if (this.props.onSelectOption) {
            this.props.onSelectOption(mention, processAddMention)
        } else {
            processAddMention()
        }
    }

    onMentionFocus(index, callback) {
        this.props.ariaProps.ariaActiveDescendantID = getMentionOptionId(this.uniqKey, index)
        this.setState({
            focusedOptionIndex: index,
        }, callback)

        // to force a re-render of the outer component to change the aria props
        this.props.store.setEditorState(this.props.store.getEditorState())
    }

    commitSelection(keyboardEvent?) {
        if (keyboardEvent) {
            keyboardEvent.preventDefault()
            keyboardEvent.stopPropagation()
        }

        if (!this.props.store.getIsOpened()) {
            return 'not-handled'
        }

        this.onMentionSelect(this.props.suggestions[this.state.focusedOptionIndex])
        return 'handled'
    }

    onKeyDown(keyboardEvent) {
        if (this.props.callbacks.keyBindingFn && this.props.callbacks.keyBindingFn(keyboardEvent)) {
            keyboardEvent.preventDefault()
            keyboardEvent.stopPropagation()
        }
    }

    openDropdown() {
        // This is a really nasty way of attaching & releasing the key related functions.
        // It assumes that the keyFunctions object will not loose its reference and
        // by this we can replace inner parameters spread over different modules.
        // This better be some registering & unregistering logic. PRs are welcome :)
        this.props.callbacks.handleReturn = this.commitSelection
        this.props.callbacks.keyBindingFn = keyboardEvent => {
            let res = true

            switch (keyboardEvent.keyCode) {
                case 40:  this.onDownArrow(keyboardEvent); break
                case 38:  this.onUpArrow(keyboardEvent); break
                case 27:  this.onEscape(keyboardEvent); break
                case 9:   this.onTab(keyboardEvent); break
                case 8: //backspace
                    // close dropdown is required because of React removeNode error
                    // triggered if mentions-popover-target is still present while
                    // trigger character is removed from darft-js
                    if (!_.isNil(this.lastSearchValue) && _.isEmpty(this.lastSearchValue)) {
                        this.closeDropdown(keyboardEvent)
                    } else {
                        res = false
                    }
                    break

                default:  res = false
            }
            return res
        }

        setAriaOpened(this.props.ariaProps, this.uniqKey, this.state.focusedOptionIndex)
        this.setState({
            isActive: true
        })
    }

    closeDropdown(keyboardEvent?) {
        if (keyboardEvent) {
            keyboardEvent.preventDefault()
            keyboardEvent.stopPropagation()
        }

        // make sure none of these callbacks are triggered
        this.props.callbacks.handleReturn = undefined
        this.props.callbacks.keyBindingFn = undefined
        setAriaClosed(this.props.ariaProps)
        this.setState({
            isActive: false
        }, ()=>{
            if (keyboardEvent) {
                this.props.onRemoveMentionTrigger(this.props.mentionTrigger)
            }
        })
    }

    updatePopover() {
        const scroll = this.scrollRef.current
        const node = (findDOMNode(scroll) as HTMLElement).getElementsByClassName("isFocused") as HTMLCollectionOf<HTMLElement>
        scroll.setScrollTop(node[0].offsetTop,true,0)
    }
}
